import { AdminRole } from '../models';
import { TournamentAdminRepository, TournamentRepository } from '../repositories';

export type TournamentPermission = 
  | 'view'           // Ver el torneo
  | 'edit_details'   // Editar nombre/descripción
  | 'manage_participants' // Añadir/eliminar participantes
  | 'update_results' // Actualizar resultados de matches
  | 'manage_admins'  // Añadir/remover administradores
  | 'delete';        // Eliminar el torneo

export interface PermissionCheck {
  hasPermission: boolean;
  userRole?: AdminRole | 'owner';
}

export class CheckTournamentPermissionUseCase {
  constructor(
    private tournamentRepository: TournamentRepository,
    private tournamentAdminRepository: TournamentAdminRepository
  ) {}

  async execute(params: {
    tournamentId: string;
    userId: string;
    permission: TournamentPermission;
  }): Promise<PermissionCheck> {
    const { tournamentId, userId, permission } = params;

    // Verificar que el torneo existe
    const tournament = await this.tournamentRepository.getById(tournamentId);
    if (!tournament) {
      return { hasPermission: false };
    }

    // Si es el owner, tiene todos los permisos
    if (tournament.userId === userId) {
      return { hasPermission: true, userRole: 'owner' };
    }

    // Buscar si es admin
    const admins = await this.tournamentAdminRepository.getByTournamentId(tournamentId);
    const userAdmin = admins.find(admin => admin.userId === userId);

    if (!userAdmin) {
      return { hasPermission: false };
    }

    // Verificar permisos según rol
    const hasPermission = this.checkRolePermission(userAdmin.role, permission);

    return {
      hasPermission,
      userRole: userAdmin.role,
    };
  }

  private checkRolePermission(role: AdminRole, permission: TournamentPermission): boolean {
    const permissions: Record<AdminRole, TournamentPermission[]> = {
      owner: [
        'view',
        'edit_details',
        'manage_participants',
        'update_results',
        'manage_admins',
        'delete',
      ],
      admin: [
        'view',
        'manage_participants',
        'update_results',
      ],
      viewer: [
        'view',
      ],
    };

    return permissions[role]?.includes(permission) || false;
  }
}